import _filter from 'lodash/filter';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _map from 'lodash/map';
import _forEach from 'lodash/forEach';
import _keys from 'lodash/keys';
import _values from 'lodash/values';
import _isEqual from 'lodash/isEqual';
import _reduce from 'lodash/reduce';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import { AppEntityActions, EntityActions, RoleDocument } from 'commons/models/marketingDashboard';

import { SCOPES_MAP, RESOURCES_MAP, PAGE_NAMES } from 'constant';

import {
	APP_OPTIONS,
	PAGE_ENTITY_OPTIONS_MAP,
	APP_TO_DASHBOARD_ACTIONS_MAP,
	APP_ACTIONS,
	AppDashboardAction,
	FormEntityActions,
	IRolesFormData
} from './constants';

const getHydratedPageConfig = (pageName: string, entityActionConfig?: EntityActions) => {
	return _map(PAGE_ENTITY_OPTIONS_MAP[pageName], ({ id: entity }) => {
		const actionConfig: { [action: string]: boolean } | undefined = _get(entityActionConfig, entity);
		return {
			entity,
			actions: _filter(_keys(actionConfig), (action) => _get(actionConfig, action, false))
		};
	});
};

const getAppLevelAction = (dashboardActions: FormEntityActions[]): string => {
	const dashboardEntityActionMap: AppDashboardAction = {};
	_forEach(dashboardActions, (dashboardEntity) => {
		dashboardEntityActionMap[dashboardEntity.entity] = dashboardEntity.actions;
	});
	return _reduce(
		Object.keys(APP_ACTIONS),
		(result, appAction) => {
			if (appAction === APP_ACTIONS.custom) {
				return result;
			}
			const nonCustomDashboardActions = APP_TO_DASHBOARD_ACTIONS_MAP[appAction];

			const isAllActionEqual = _reduce(
				Object.keys(nonCustomDashboardActions),
				(allActionsEqual, dashboardEntity) => {
					// console.log(allActionsEqual, dashboardEntity, 'wow');
					return (
						allActionsEqual &&
						_isEqual(
							_sortBy(nonCustomDashboardActions[dashboardEntity]),
							_sortBy(dashboardEntityActionMap[dashboardEntity])
						)
					);
				},
				true
			);
			if (isAllActionEqual) {
				return appAction;
			}
			return result;
		},
		APP_ACTIONS.custom
	);
};

export const hydrateFormData = (data?: RoleDocument): IRolesFormData => {
	const appEntityActions = _get(data, ['roleActions', 'appEntity']);
	const settingsActions = _get(data, ['roleActions', 'settings']);

	return {
		role: _get(data, 'role', ''),
		_id: _get(data, '_id'),
		settings: getHydratedPageConfig(PAGE_NAMES.settings, settingsActions),
		appEntity: _map(APP_OPTIONS, ({ id: app }) => {
			// DONT USE `${app}.dashboardConfig` in pathname(will run in js but give error in TS ))
			const dashboardActions = _get(appEntityActions, [app, 'dashboard']);
			const gameAdminActions = _get(appEntityActions, [app, 'gameAdmin']);
			const hydratedDashboardActions = getHydratedPageConfig(PAGE_NAMES.dashboard, dashboardActions);
			const hydratedGameAdminActions = getHydratedPageConfig(PAGE_NAMES.gameAdmin, gameAdminActions);
			const appLevelAction: string = getAppLevelAction(hydratedDashboardActions);

			return {
				app,
				dashboard: hydratedDashboardActions,
				gameAdmin: hydratedGameAdminActions,
				appLevelAction
			};
		})
	};
};

const getEntityScope = (entity: string, pageName: string) => {
	const reqdScopes = SCOPES_MAP[pageName];
	const reqdResources = RESOURCES_MAP[pageName];
	return reqdScopes[reqdResources[entity]];
};

const getDehydratedPageConfig = (pageName: string, pageConfig: FormEntityActions[]): EntityActions | undefined => {
	const dehydratedPageConfig: EntityActions = {};
	_forEach(pageConfig, ({ entity, actions }) => {
		// not storing keys for those entities which have no actions access given to it
		if (actions?.length > 0) {
			const entityScopes = getEntityScope(entity, pageName);
			dehydratedPageConfig[entity] = {};
			_forEach(_values(entityScopes), (scope) => {
				dehydratedPageConfig[entity][scope] = _includes(actions, scope);
			});
		}
	});
	if (Object.keys(dehydratedPageConfig).length > 0) {
		return dehydratedPageConfig;
	}
	return undefined;
};
const getNonCustomDashboardActions = (appLevelAction: string | undefined, dashboardActions: FormEntityActions[]) => {
	return _map(dashboardActions, (dashboardEntity) => {
		let actions: string[] = [];
		if (appLevelAction) {
			actions = APP_TO_DASHBOARD_ACTIONS_MAP[appLevelAction][dashboardEntity.entity];
		}
		return { ...dashboardEntity, actions };
	});
};
export const dehydrateFormData = (formData: IRolesFormData): RoleDocument => {
	const { role, _id, appEntity: formattedAppEntityActions, settings: formattedSettingsActions } = formData;

	// const appEntityActionConfig: IAppEntityActionConfig = {};
	const appEntityActions: AppEntityActions = {};

	_forEach(formattedAppEntityActions, ({ dashboard: dashboardActions, gameAdmin: gameAdminActions, app, appLevelAction }) => {
		let nonCustomDashboardActions;
		if (appLevelAction !== APP_ACTIONS.custom) {
			nonCustomDashboardActions = getNonCustomDashboardActions(appLevelAction, dashboardActions);
		}
		const dehydratedDashboardActions = getDehydratedPageConfig(
			PAGE_NAMES.dashboard,
			nonCustomDashboardActions || dashboardActions
		);
		const dehydratedGameAdminActions = getDehydratedPageConfig(PAGE_NAMES.gameAdmin, gameAdminActions);
		appEntityActions[app] = {
			...(!_isEmpty(dehydratedDashboardActions) && { dashboard: dehydratedDashboardActions }),
			...(!_isEmpty(dehydratedGameAdminActions) && { gameAdmin: dehydratedGameAdminActions })
		};
	});

	const dehydratedSettingsConfig = getDehydratedPageConfig(PAGE_NAMES.settings, formattedSettingsActions);

	return {
		role,
		_id,
		roleActions: {
			appEntity: appEntityActions,
			...(!_isEmpty(dehydratedSettingsConfig) && { settings: dehydratedSettingsConfig })
		}
	};
};
